/**
 * Interaction tracking types
 * For tracking interactions at organization and location level
 */

import type { BaseEntity, UUID } from "./common.types";

// Interaction purpose (why)
export type InteractionPurpose =
  | "meeting"
  | "review"
  | "check-in"
  | "follow-up"
  | "other";

// Interaction type/method (how)
// Note: Uses underscores to match database ENUM values
export type InteractionMethod =
  | "phone_call"
  | "meeting"
  | "email"
  | "in_person"
  | "event";

// Legacy interaction type enum for backward compatibility
export enum InteractionType {
  EMAIL = "email",
  PHONE_CALL = "phone_call",
  MEETING = "meeting",
  NOTE = "note",
  SMS = "sms",
  SOCIAL_MEDIA = "social_media",
  CHAT = "chat",
  FORM_SUBMISSION = "form_submission",
  DOCUMENT_SHARED = "document_shared",
  OTHER = "other",
  // Reward-related types
  VOUCHER_ISSUED = "voucher_issued",
  OFFER_LINK_SENT = "offer_link_sent",
  OFFER_LINK_COPIED = "offer_link_copied",
  PHYSICAL_DISTRIBUTED = "physical_distributed",
  PHYSICAL_SHIPPED = "physical_shipped",
  REWARD_REDEEMED = "reward_redeemed",
}

// Reward type for interactions
export type InteractionRewardType =
  | "voucher"
  | "url_offer"
  | "physical_good"
  | "subscription";

// Interaction direction
export enum InteractionDirection {
  INBOUND = "inbound",
  OUTBOUND = "outbound",
}

// Interaction outcome
export enum InteractionOutcome {
  SUCCESSFUL = "successful",
  NO_ANSWER = "no_answer",
  LEFT_MESSAGE = "left_message",
  FOLLOW_UP_NEEDED = "follow_up_needed",
  COMPLETED = "completed",
  DECLINED = "declined", // Legacy - kept for backward compatibility
  DECLINED_OFFER = "declined_offer", // Member declined offer/payment arrangement
  DECLINED_RESOLUTION = "declined_resolution", // Member declined proposed resolution
  CALLBACK_SCHEDULED = "callback_scheduled", // Member requested callback
  OTHER = "other",
}

// Interaction participant (contact or user)
export interface InteractionParticipant {
  id: UUID;
  type: "contact" | "user";
  name: string;
}

// Interaction attachment
export interface InteractionAttachment {
  id: UUID;
  interaction_id: UUID;
  file_name: string;
  file_url: string;
  file_size: number; // in bytes (max 5GB = 5368709120 bytes)
  file_type: string; // MIME type
  uploaded_by: UUID;
  created_at: string;
}

// Interaction
export interface Interaction extends BaseEntity {
  // NULL is allowed only for event-reward rows (migration 2026-10-05-220: the organization follows the
  // event) and for support-task activity filed under an organization-less customer (2026-10-06-321).
  organization_id: UUID | null;
  location_id?: UUID;
  contact_id?: UUID; // Optional for backward compatibility
  user_id: UUID; // User who logged the interaction

  // New required fields from spec
  title: string;
  purpose: InteractionPurpose;
  method: InteractionMethod;
  interaction_date: string; // ISO date
  interaction_time?: string; // HH:MM format
  description?: string;
  participants?: InteractionParticipant[]; // Allow individuals and users to be tagged
  attachments?: InteractionAttachment[]; // Support up to 5GB per attachment

  // Legacy fields for backward compatibility
  type?: InteractionType;
  direction?: InteractionDirection;
  subject?: string;

  // Organization servicing log (business pages slice 3, migration 2026-10-06-321). call_plan and
  // results are separate fields; `description` keeps its own meaning and is shown as "Notes".
  call_plan?: string | null;
  results?: string | null;
  subject_id?: UUID | null; // interaction_subjects ("Subject / Task Type")
  activity_type_id?: UUID | null; // interaction_activity_types
  goal_session_id?: UUID | null; // interaction_goal_sessions ("Related Goal / Session")
  // Internal-only servicing record: RLS lets only active internal staff read or write it.
  is_internal?: boolean;

  // Outcome and follow-up ("Next Task Due" on the servicing form)
  outcome?: InteractionOutcome;
  next_follow_up_date?: string;
  follow_up_task_id?: UUID;

  // Timing
  duration_minutes?: number;

  // Related entities
  event_id?: UUID;
  task_id?: UUID;
  support_task_id?: UUID; // Reference to support task if interaction was logged from task resolution
  voucher_id?: UUID; // Reference to voucher if interaction resulted in voucher creation
  cta_id?: UUID; // Reference to CTA that triggered this interaction

  // Reward tracking
  reward_type?: InteractionRewardType; // 'voucher', 'url_offer', 'physical_good', 'subscription'
  url_shared?: string; // URL that was shared with the contact (for offer links)

  // Communication details
  email_subject?: string;
  email_message_id?: string;
  phone_number?: string;
  call_recording_url?: string;
  meeting_notes?: string;
  meeting_url?: string;

  // Categorization
  tags?: string[];
  category?: string;
  sentiment?: InteractionSentiment;

  // Metadata
  metadata?: Record<string, unknown>;
}

// Interaction sentiment
export enum InteractionSentiment {
  POSITIVE = "positive",
  NEUTRAL = "neutral",
  NEGATIVE = "negative",
}

// Interaction template
export interface InteractionTemplate extends BaseEntity {
  organization_id: UUID;
  name: string;
  type: InteractionType;
  subject_template?: string;
  description_template: string;
  tags?: string[];
  category?: string;
}

// Interaction filters
export interface InteractionFilters {
  organization_id?: UUID;
  location_id?: UUID;
  contact_id?: UUID;
  user_id?: UUID;
  purpose?: InteractionPurpose[];
  method?: InteractionMethod[];
  type?: InteractionType[]; // Legacy
  direction?: InteractionDirection;
  outcome?: InteractionOutcome[];
  date_from?: string;
  date_to?: string;
  tags?: string[];
  category?: string;
  sentiment?: InteractionSentiment;
  search?: string;
  subject_id?: UUID;
  activity_type_id?: UUID;
}

/** One value of a servicing picklist (interaction_subjects / _activity_types / _goal_sessions). */
export interface InteractionLookupValue {
  id: UUID;
  label: string;
  sort_order: number;
  is_active: boolean;
  is_placeholder: boolean;
  created_at?: string;
  updated_at?: string;
}

export type InteractionLookupList =
  | "subjects"
  | "activity_types"
  | "goal_sessions";

// Interaction statistics
export interface InteractionStats {
  total: number;
  by_type: Record<InteractionType, number>;
  by_direction: Record<InteractionDirection, number>;
  by_outcome: Record<InteractionOutcome, number>;
  by_sentiment: Record<InteractionSentiment, number>;
  this_week: number;
  this_month: number;
  average_per_contact: number;
}

// Communication log (simplified view)
export interface CommunicationLog {
  id: UUID;
  contact_name: string;
  user_name: string;
  type: InteractionType;
  subject?: string;
  date: string;
  outcome?: InteractionOutcome;
  sentiment?: InteractionSentiment;
}

// Email tracking
export interface EmailTracking extends BaseEntity {
  interaction_id: UUID;
  email_id: string;
  sent_at: string;
  delivered_at?: string;
  opened_at?: string;
  clicked_at?: string;
  bounced: boolean;
  bounce_reason?: string;
  open_count: number;
  click_count: number;
}
